/**
 * Drag release (PRP-WEB-2 W1.1). The cleanup the phone controller ran on release,
 * moved into the core so the web editor runs the same steps in the same order:
 *
 * 1. the drag's own end (`endSegmentDrag` for a wall, `endRoomDrag` for a room);
 * 2. K6 (DD-SF4.9): a segment reading whose T moved off the wall goes (`pruneSegmentSpans`);
 * 3. FIELD-5: damage spans follow the wall that moved (`reconcileDamage`);
 * 4. no change: the rooms are what they were, nothing commits;
 * 5. read-only: a room the caller may not edit (an outdated phone format, a v1 room on the
 *    web) changed, nothing commits and the result names the room.
 *
 * `working` is mutated (steps 1-3), exactly as the phone did. The caller keeps the
 * selection, the toasts, History and persistence.
 */
import { endRoomDrag, type WallDrag } from './edit';
import { endSegmentDrag } from './breaks';
import { pruneSegmentSpans } from './segments';
import { reconcileDamage, type DroppedSpan } from './damage';
import { findRoom } from './geom';
import type { Measurement, Plan } from './types';

export interface FinishOpts {
  /** Ids of rooms this caller may not change (the phone: rooms the server refused; the web: DD7). */
  readOnly?: readonly string[];
}

interface FinishBase {
  /**
   * Wall drags: corner ids merged on release (removed -> kept). Set on every outcome:
   * a drag that changed nothing still merges its connectors back, and a selection on
   * the dragged wall must follow (the phone repoints it before any check).
   */
  merged: Map<number, number>;
}

export type DragFinish =
  /** The rooms are unchanged: keep `before`. */
  | (FinishBase & { kind: 'unchanged' })
  /** A read-only room changed: keep `before`; `room` names it. */
  | (FinishBase & { kind: 'blocked'; room: { id: string; name: string } })
  /** Commit `working` (checkpoint `before` first). */
  | (FinishBase & {
    kind: 'changed';
    /** A snapped wall drag removed a break (the phone toasts "Break removed"). */
    breakRemoved: boolean;
    /** K6: segment readings dropped because the wall that met there moved. */
    pruned: { rid: string; valueFt: number }[];
    /** FIELD-5: damage spans dropped. */
    droppedDamage: DroppedSpan[];
    /** K8: measurements the wall drag changed, now rough. */
    rough: Measurement[];
  });

/** The first read-only room whose stored form changed between `before` and `after`. */
export function readOnlyChanged(before: Plan, after: Plan, ids: readonly string[]): { id: string; name: string } | null {
  for (const id of ids) {
    const was = findRoom(before, id);
    if (was && JSON.stringify(was) !== JSON.stringify(findRoom(after, id))) return { id, name: was.name };
  }
  return null;
}

function finish(
  before: Plan, working: Plan, opts: FinishOpts, extra: { merged: Map<number, number>; breakRemoved: boolean; rough: Measurement[] },
): DragFinish {
  const pruned = pruneSegmentSpans(working);
  const droppedDamage = reconcileDamage(before, working);
  if (JSON.stringify(before.rooms) === JSON.stringify(working.rooms)) return { kind: 'unchanged', merged: extra.merged };
  const room = readOnlyChanged(before, working, opts.readOnly ?? []);
  if (room) return { kind: 'blocked', merged: extra.merged, room };
  return { kind: 'changed', ...extra, pruned, droppedDamage };
}

/**
 * Release of a wall drag. `drag` is the `beginWallDrag` drag (null for a shared-wall
 * drag, `dragSharedWall`, which has no end step); `snapped` is whether the last
 * preview snapped to a neighbour's line (J7: a snapped release may remove a break).
 */
export function finishWallDrag(before: Plan, working: Plan, drag: WallDrag | null, snapped: boolean, opts: FinishOpts = {}): DragFinish {
  const merged = drag ? endSegmentDrag(working, drag, snapped) : new Map<number, number>();
  return finish(before, working, opts, { merged, breakRemoved: !!drag && snapped && merged.size > 0, rough: drag?.rough ?? [] });
}

/** Release of a room drag (`beginRoomDrag` / `dragRoom`). */
export function finishRoomDrag(before: Plan, working: Plan, opts: FinishOpts = {}): DragFinish {
  endRoomDrag(working);
  return finish(before, working, opts, { merged: new Map(), breakRemoved: false, rough: [] });
}

/** Release of an opening drag (`beginOpeningDrag` / `dragOpening`). */
export function finishOpeningDrag(before: Plan, working: Plan, opts: FinishOpts = {}): DragFinish {
  return finish(before, working, opts, { merged: new Map(), breakRemoved: false, rough: [] });
}
